/**
 * building-extrude.ts — extract footprints (buildings, docks) from the
 * compiled terrain semantics and produce world-space oriented boxes
 * ready to instance.
 *
 * Algorithm:
 *  1. Walk the annotation raster; mark every pixel of the requested class
 *     into a binary mask.
 *  2. Run connected-components labelling (4-neighbour BFS).
 *  3. For each component: compute centroid + 2x2 covariance → eigenvectors
 *     give the major/minor axes of the oriented bounding box. The OBB extent
 *     covers the painted pixel set, the OBB centre gives world position,
 *     and the major-axis angle becomes footprint yaw.
 *
 * Limitations:
 *  - OBB approximates rectangular shapes well. Paint L-shapes as TWO
 *    separate rectangles with a gap of one transparent pixel between them.
 */

import type { TerrainAsset } from "../assets/TerrainAsset";
import {
  buildTerrainSurfaceMask,
  sampleTerrainHeight,
  type TerrainSurface,
} from "../assets/TerrainAsset";

export interface AnnotationFootprint {
  /** Centre of the footprint in world coords (metres). */
  x: number;
  z: number;
  /** Ground/water height (m above water level) under the centre. */
  y: number;
  /** Length along the major axis (metres). */
  sx: number;
  /** Length along the minor axis (metres). */
  sz: number;
  /** Yaw of the major axis (rad), 0 = +X. */
  yaw: number;
  /** Number of source annotation pixels (≈ area in px²). */
  pixelCount: number;
}

const MIN_PIXELS = 6; // discard noise / single-stamp blobs

function extractFootprints(
  terrain: TerrainAsset,
  surface: TerrainSurface,
): AnnotationFootprint[] {
  const surfaceMask = buildTerrainSurfaceMask(terrain, surface);
  if (!surfaceMask) return [];
  const aw = surfaceMask.width;
  const ah = surfaceMask.height;

  // 1. Build binary mask.
  const mask = surfaceMask.mask;

  // 2. Connected components — flood-fill via stack, 4-neighbour.
  const labels = new Int32Array(aw * ah);
  const components: Array<Array<[number, number]>> = [];
  const stack: Array<[number, number]> = [];
  let nextLabel = 0;
  for (let y = 0; y < ah; y++) {
    for (let x = 0; x < aw; x++) {
      const idx = y * aw + x;
      if (!mask[idx] || labels[idx]) continue;
      nextLabel++;
      labels[idx] = nextLabel;
      const pixels: Array<[number, number]> = [];
      stack.length = 0;
      stack.push([x, y]);
      while (stack.length) {
        const [cx, cy] = stack.pop()!;
        pixels.push([cx, cy]);
        const neighbours: Array<[number, number]> = [
          [cx + 1, cy],
          [cx - 1, cy],
          [cx, cy + 1],
          [cx, cy - 1],
        ];
        for (const [nx, ny] of neighbours) {
          if (nx < 0 || nx >= aw || ny < 0 || ny >= ah) continue;
          const nIdx = ny * aw + nx;
          if (!mask[nIdx] || labels[nIdx]) continue;
          labels[nIdx] = nextLabel;
          stack.push([nx, ny]);
        }
      }
      components.push(pixels);
    }
  }

  // 3. PCA per component → OBB.
  const wM = terrain.worldWidthM;
  const hM = terrain.worldHeightM;
  const halfW = wM * 0.5;
  const halfH = hM * 0.5;
  const pxToWorldX = wM / aw;
  const pxToWorldZ = hM / ah;

  const out: AnnotationFootprint[] = [];
  for (const comp of components) {
    if (comp.length < MIN_PIXELS) continue;

    const worldPts: Array<[number, number]> = comp.map(([px, py]) => [
      -halfW + (px + 0.5) * pxToWorldX,
      -halfH + (py + 0.5) * pxToWorldZ,
    ]);

    let mx = 0;
    let mz = 0;
    for (const [x, z] of worldPts) {
      mx += x;
      mz += z;
    }
    mx /= worldPts.length;
    mz /= worldPts.length;

    let cxx = 0;
    let cxz = 0;
    let czz = 0;
    for (const [x, z] of worldPts) {
      const dx = x - mx;
      const dz = z - mz;
      cxx += dx * dx;
      cxz += dx * dz;
      czz += dz * dz;
    }
    const inv = 1 / worldPts.length;
    cxx *= inv;
    cxz *= inv;
    czz *= inv;

    const tr = cxx + czz;
    const det = cxx * czz - cxz * cxz;
    const disc = Math.max(0, tr * tr * 0.25 - det);
    const sqrtDisc = Math.sqrt(disc);
    const l1 = tr * 0.5 + sqrtDisc;
    let ex = cxz;
    let ez = l1 - cxx;
    if (Math.abs(ex) < 1e-9 && Math.abs(ez) < 1e-9) {
      ex = 1;
      ez = 0;
    }
    const eLen = Math.hypot(ex, ez);
    ex /= eLen;
    ez /= eLen;
    const fx = -ez;
    const fz = ex;

    let uMin = Infinity;
    let uMax = -Infinity;
    let vMin = Infinity;
    let vMax = -Infinity;
    for (const [x, z] of worldPts) {
      const dx = x - mx;
      const dz = z - mz;
      const u = dx * ex + dz * ez;
      const v = dx * fx + dz * fz;
      if (u < uMin) uMin = u;
      if (u > uMax) uMax = u;
      if (v < vMin) vMin = v;
      if (v > vMax) vMax = v;
    }

    const uMid = (uMin + uMax) * 0.5;
    const vMid = (vMin + vMax) * 0.5;
    const cxWorld = mx + ex * uMid + fx * vMid;
    const czWorld = mz + ez * uMid + fz * vMid;

    const padU = pxToWorldX * 0.5;
    const padV = pxToWorldZ * 0.5;
    let sx = Math.max(0.5, uMax - uMin + padU * 2);
    let sz = Math.max(0.5, vMax - vMin + padV * 2);
    let yaw = Math.atan2(ez, ex);
    // Normalise so sx is ALWAYS the longer axis. Simplifies downstream code
    // (gable ridge along sx, dock pile rows along sx, etc.).
    if (sz > sx) {
      [sx, sz] = [sz, sx];
      yaw += Math.PI / 2;
    }

    const groundY = sampleTerrainHeight(terrain, cxWorld, czWorld);
    out.push({
      x: cxWorld,
      z: czWorld,
      y: groundY,
      sx,
      sz,
      yaw,
      pixelCount: comp.length,
    });
  }

  return out;
}

export interface BuildingFootprint extends AnnotationFootprint {
  /** Building eaves height (m). */
  height: number;
}

export function extractBuildingsFromAnnotation(
  terrain: TerrainAsset,
): BuildingFootprint[] {
  const buildingHeight = terrain.meta.building_default_height_m ?? 6;
  return extractFootprints(terrain, "building").map((f) => ({
    ...f,
    // Bury ground rounding: clamp y to ≥0 and sink 5 cm so the wall meets
    // ground without z-fighting on slopes.
    y: Math.max(0, f.y) - 0.05,
    height: buildingHeight,
  }));
}

export interface DockFootprint extends AnnotationFootprint {
  /** Deck top height above water level (m). */
  deckHeight: number;
  /** Mooring style supplied by the L3 dock feature. */
  mooringStyle: MooringStyle;
}

export interface YBoomArmFootprint {
  /** World-space centre of the arm (metres). */
  x: number;
  z: number;
  /** Arm length along its painted axis (metres). */
  length: number;
  /** Arm yaw — direction the arm points (rad), 0 = +X. */
  yaw: number;
  /** Deck top height above water (m). */
  deckHeight: number;
}

export function extractDocksFromAnnotation(terrain: TerrainAsset): {
  docks: DockFootprint[];
  yBoomArms: YBoomArmFootprint[];
} {
  const deckHeight = terrain.meta.dock_default_height_m ?? 0.6;
  return {
    docks: extractFootprints(terrain, "dock").map((f) => ({
      ...f,
      deckHeight,
      mooringStyle: sampleDockMooringStyle(terrain, f),
    })),
    yBoomArms: [],
  };
}

/**
 * Fit a straight line through a chain of skeleton pixels via PCA, then
 * project all pixels onto that line and return centre + length + yaw of
 * the resulting segment in WORLD coords. Filters out the zigzag of
 * Zhang-Suen output so collinear branches across a junction don't get
 * rendered as wedges.
 */
export function fitStraightSegment(
  pixels: ReadonlyArray<readonly [number, number]>,
  halfWorldX: number,
  halfWorldZ: number,
  pxToWorldX: number,
  pxToWorldZ: number,
): { cx: number; cz: number; length: number; yaw: number } | null {
  if (pixels.length < 2) return null;
  // Convert to world coords first so PCA respects non-square pixels.
  const wx: number[] = new Array(pixels.length);
  const wz: number[] = new Array(pixels.length);
  let mx = 0;
  let mz = 0;
  for (let i = 0; i < pixels.length; i++) {
    const px = pixels[i]![0];
    const py = pixels[i]![1];
    const x = -halfWorldX + (px + 0.5) * pxToWorldX;
    const z = -halfWorldZ + (py + 0.5) * pxToWorldZ;
    wx[i] = x;
    wz[i] = z;
    mx += x;
    mz += z;
  }
  mx /= pixels.length;
  mz /= pixels.length;
  let cxx = 0;
  let cxz = 0;
  let czz = 0;
  for (let i = 0; i < pixels.length; i++) {
    const dx = wx[i]! - mx;
    const dz = wz[i]! - mz;
    cxx += dx * dx;
    cxz += dx * dz;
    czz += dz * dz;
  }
  const inv = 1 / pixels.length;
  cxx *= inv;
  cxz *= inv;
  czz *= inv;
  const tr = cxx + czz;
  const det = cxx * czz - cxz * cxz;
  const disc = Math.max(0, tr * tr * 0.25 - det);
  const sqrtDisc = Math.sqrt(disc);
  const l1 = tr * 0.5 + sqrtDisc;
  let ex = cxz;
  let ez = l1 - cxx;
  if (Math.abs(ex) < 1e-9 && Math.abs(ez) < 1e-9) {
    ex = 1;
    ez = 0;
  }
  const eLen = Math.hypot(ex, ez);
  ex /= eLen;
  ez /= eLen;
  // Project pixels onto the major axis to find segment extent.
  let uMin = Infinity;
  let uMax = -Infinity;
  for (let i = 0; i < pixels.length; i++) {
    const dx = wx[i]! - mx;
    const dz = wz[i]! - mz;
    const u = dx * ex + dz * ez;
    if (u < uMin) uMin = u;
    if (u > uMax) uMax = u;
  }
  const length = uMax - uMin;
  const uMid = (uMin + uMax) * 0.5;
  return {
    cx: mx + ex * uMid,
    cz: mz + ez * uMid,
    length,
    yaw: Math.atan2(ez, ex),
  };
}

/**
 * 8-connected components labelling on a binary mask. Diagonal
 * connectivity matters for thin hand-drawn lines (a 1-px wide T may
 * only touch its neighbour through diagonals).
 */
export function connectedComponents8(
  mask: Uint8Array,
  aw: number,
  ah: number,
): Array<Array<[number, number]>> {
  const labels = new Int32Array(aw * ah);
  const components: Array<Array<[number, number]>> = [];
  const stack: Array<[number, number]> = [];
  let nextLabel = 0;
  for (let y = 0; y < ah; y++) {
    for (let x = 0; x < aw; x++) {
      const idx = y * aw + x;
      if (!mask[idx] || labels[idx]) continue;
      nextLabel++;
      labels[idx] = nextLabel;
      const pixels: Array<[number, number]> = [];
      stack.length = 0;
      stack.push([x, y]);
      while (stack.length) {
        const [cx, cy] = stack.pop()!;
        pixels.push([cx, cy]);
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = cx + dx;
            const ny = cy + dy;
            if (nx < 0 || nx >= aw || ny < 0 || ny >= ah) continue;
            const nIdx = ny * aw + nx;
            if (!mask[nIdx] || labels[nIdx]) continue;
            labels[nIdx] = nextLabel;
            stack.push([nx, ny]);
          }
        }
      }
      components.push(pixels);
    }
  }
  return components;
}

/**
 * Zhang-Suen morphological thinning. Mutates `m` in place to a single-
 * pixel-wide skeleton of the input binary mask. Standard reference
 * implementation — two sub-iterations per pass, repeat until stable.
 */
function thinZhangSuen(m: Uint8Array, w: number, h: number): void {
  const get = (x: number, y: number) =>
    x < 0 || x >= w || y < 0 || y >= h ? 0 : m[y * w + x]!;
  const toRemove: number[] = [];
  let changed = true;
  while (changed) {
    changed = false;
    for (let pass = 0; pass < 2; pass++) {
      toRemove.length = 0;
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          if (!m[y * w + x]) continue;
          const p2 = get(x, y - 1);
          const p3 = get(x + 1, y - 1);
          const p4 = get(x + 1, y);
          const p5 = get(x + 1, y + 1);
          const p6 = get(x, y + 1);
          const p7 = get(x - 1, y + 1);
          const p8 = get(x - 1, y);
          const p9 = get(x - 1, y - 1);
          const B = p2 + p3 + p4 + p5 + p6 + p7 + p8 + p9;
          if (B < 2 || B > 6) continue;
          const seq = [p2, p3, p4, p5, p6, p7, p8, p9, p2];
          let A = 0;
          for (let i = 0; i < 8; i++) {
            if (seq[i] === 0 && seq[i + 1] === 1) A++;
          }
          if (A !== 1) continue;
          if (pass === 0) {
            if (p2 * p4 * p6 !== 0) continue;
            if (p4 * p6 * p8 !== 0) continue;
          } else {
            if (p2 * p4 * p8 !== 0) continue;
            if (p2 * p6 * p8 !== 0) continue;
          }
          toRemove.push(y * w + x);
        }
      }
      if (toRemove.length) {
        changed = true;
        for (const idx of toRemove) m[idx] = 0;
      }
    }
  }
}

/**
 * Thin a connected blob to a 1-px skeleton, then split it at branch
 * points (skeleton pixels with ≥3 neighbours) into individual chains.
 * Returns one chain of `[x, y]` pixels (in the original raster's
 * coordinate space) per branch — a straight pier yields one chain;
 * a T-shape yields three (one per arm of the T).
 */
export function decomposeBlobToSkeletonBranches(
  pixels: Array<[number, number]>,
  aw: number,
  ah: number,
): Array<Array<[number, number]>> {
  // Crop to bbox + 2 px padding to keep thinning fast.
  let xMin = aw;
  let yMin = ah;
  let xMax = -1;
  let yMax = -1;
  for (const [x, y] of pixels) {
    if (x < xMin) xMin = x;
    if (x > xMax) xMax = x;
    if (y < yMin) yMin = y;
    if (y > yMax) yMax = y;
  }
  const PAD = 2;
  xMin = Math.max(0, xMin - PAD);
  yMin = Math.max(0, yMin - PAD);
  xMax = Math.min(aw - 1, xMax + PAD);
  yMax = Math.min(ah - 1, yMax + PAD);
  const w = xMax - xMin + 1;
  const h = yMax - yMin + 1;
  const m = new Uint8Array(w * h);
  for (const [x, y] of pixels) m[(y - yMin) * w + (x - xMin)] = 1;

  thinZhangSuen(m, w, h);

  const deg = new Int8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!m[y * w + x]) continue;
      let d = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
          if (m[ny * w + nx]) d++;
        }
      }
      deg[y * w + x] = d;
    }
  }

  const visited = new Uint8Array(w * h);
  const branches: Array<Array<[number, number]>> = [];
  const neigh = (
    x: number,
    y: number,
    skipVisited: boolean,
  ): Array<[number, number]> => {
    const out: Array<[number, number]> = [];
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
        if (!m[ny * w + nx]) continue;
        if (skipVisited && visited[ny * w + nx]) continue;
        out.push([nx, ny]);
      }
    }
    return out;
  };

  const walk = (
    startX: number,
    startY: number,
    fromX: number,
    fromY: number,
  ): Array<[number, number]> => {
    const branch: Array<[number, number]> = [
      [fromX, fromY],
      [startX, startY],
    ];
    visited[startY * w + startX] = 1;
    let cx = startX;
    let cy = startY;
    while (true) {
      const d = deg[cy * w + cx];
      if (d >= 3 || d === 1) break;
      const ns = neigh(cx, cy, true);
      if (ns.length === 0) break;
      const [nx, ny] = ns[0]!;
      branch.push([nx, ny]);
      if (deg[ny * w + nx] >= 3) break; // include junction, stop
      visited[ny * w + nx] = 1;
      cx = nx;
      cy = ny;
    }
    return branch;
  };

  // Start from every endpoint (deg === 1).
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (deg[y * w + x] !== 1 || visited[y * w + x]) continue;
      visited[y * w + x] = 1;
      const ns = neigh(x, y, true);
      if (ns.length === 0) continue;
      const [nx, ny] = ns[0]!;
      branches.push(walk(nx, ny, x, y));
    }
  }
  // Then walk every still-unvisited neighbour of each branch point —
  // catches segments connecting two junctions (no endpoint).
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (deg[y * w + x] < 3) continue;
      const ns = neigh(x, y, true);
      for (const [nx, ny] of ns) {
        branches.push(walk(nx, ny, x, y));
      }
    }
  }
  // Map back to the parent raster's coordinate frame.
  return branches.map((b) =>
    b.map(([x, y]) => [x + xMin, y + yMin] as [number, number]),
  );
}

/**
 * Inspect the painted pixels inside a dock footprint and majority-vote
 * which mooring style was used. Falls back to "dock-only" if no magenta
 * pixels matched (shouldn't happen for a dock footprint, but keeps the
 * type total).
 */
function sampleDockMooringStyle(
  terrain: TerrainAsset,
  f: AnnotationFootprint,
): MooringStyle {
  void terrain;
  void f;
  return "dock-only";
}

/**
 * Build a `DockSpec` from an annotation footprint, inheriting defaults from
 * `defaultDockSpec()` and scaling pile/bollard counts to the dock size.
 *
 * Conventions:
 *  • length = sx (longer axis)  → ridge of the dock
 *  • width  = sz (shorter axis) → cross-deck
 *  • bollards on both long edges, ~3.5 m apart
 *  • piles on both long edges, ~2.5 m apart, min 2 per side
 *  • no gangway (we don't know where the shore is from the OBB alone;
 *    the user paints the connection to land directly into the footprint)
 */
import { defaultDockSpec, type DockSpec, type MooringStyle } from "./Dock";

export function dockSpecFromFootprint(f: DockFootprint, id: string): DockSpec {
  const base = defaultDockSpec(id);
  const length = Math.max(2, f.sx);
  const width = Math.max(0.8, f.sz);
  const halfL = length * 0.5;
  const halfW = width * 0.5;

  const pilesPerSide = Math.max(2, Math.round(length / 2.5));
  const bollardsPerSide = Math.max(1, Math.round(length / 3.5));

  const inset = Math.min(0.18, width * 0.15);
  const bollards = [];
  for (let side = 0; side < 2; side++) {
    const z = side === 0 ? -halfW + inset : +halfW - inset;
    const tag = side === 0 ? "P" : "S";
    for (let i = 0; i < bollardsPerSide; i++) {
      const t = bollardsPerSide === 1 ? 0.5 : (i + 0.5) / bollardsPerSide;
      const x = -halfL + t * length;
      bollards.push({ id: `${id}_${tag}${i + 1}`, x, z });
    }
  }

  return {
    ...base,
    id,
    length,
    width,
    pilesPerSide,
    bollards,
    gangway: undefined,
    mooringStyle: f.mooringStyle,
  };
}

/**
 * Trim a dock footprint so its short ends finish exactly at the water
 * line: walk both short ends inward in small steps and clip away any
 * portion whose footprint pixel sits on dry land. Mutates `spec.length`
 * and returns the world-space delta that must be added to the dock's
 * `posX`/`posZ` so the deck stays anchored over water.
 *
 * The caller is expected to apply the returned delta:
 *   const d = trimDockToShore(spec, f, terrain, waterLevel);
 *   posX = f.x + d.dx;  posZ = f.z + d.dz;
 *
 * If the entire footprint sits on land the dock is collapsed to a tiny
 * stub — caller should drop it (returns `{collapsed: true}`).
 */
export function trimDockToShore(
  spec: DockSpec,
  footprint: { x: number; z: number; yaw: number },
  terrain: TerrainAsset,
  _waterLevel: number,
): { dx: number; dz: number; collapsed: boolean } {
  const halfL = spec.length * 0.5;
  const cosY = Math.cos(footprint.yaw);
  const sinY = Math.sin(footprint.yaw);
  const STEP = 0.25;
  // For each side, find how far inward we must move before the ground
  // drops below water level (i.e. the dock starts being over water).
  const trimSide = (side: -1 | 1): number => {
    let trim = 0;
    while (trim < spec.length - 0.5) {
      const lx = side * (halfL - trim - STEP * 0.5);
      const wx = footprint.x + cosY * lx;
      const wz = footprint.z + sinY * lx;
      const groundH = sampleTerrainHeight(terrain, wx, wz);
      // Treat anything > 0 as land — this is the strict water line.
      if (groundH <= 0.0) return trim;
      trim += STEP;
    }
    return trim;
  };
  const trimPlus = trimSide(+1);
  const trimMinus = trimSide(-1);
  const newLength = spec.length - trimPlus - trimMinus;
  if (newLength < 1.0) {
    return { dx: 0, dz: 0, collapsed: true };
  }
  spec.length = newLength;
  // Centre shifts toward whichever end was trimmed less.
  // Original centre is at local x=0; new centre lies at local
  //   x = (-halfL + trimMinus + newLength/2)
  // which simplifies to (trimMinus - trimPlus) / 2 in dock-local coords.
  const localShift = (trimMinus - trimPlus) * 0.5;
  return {
    dx: cosY * localShift,
    dz: sinY * localShift,
    collapsed: false,
  };
}
